import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Lightbulb, TrendingUp } from "lucide-react";

const GRADE_COLOR = {
  A: "bg-brand-green text-white",
  B: "bg-[#7DBE3C] text-white",
  C: "bg-brand-yellow text-ink",
  D: "bg-[#EE7A2A] text-white",
  F: "bg-brand-red text-white",
};

export default function MenuHealthScore() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await apiClient.get("/my/restaurant/health-score");
        setData(d);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="text-mute py-8 text-center">Calcul du score…</div>;
  if (!data) return null;
  const gradeClass = GRADE_COLOR[data.grade] || "bg-elevated";

  return (
    <div className="space-y-4" data-testid="menu-health-score">
      <div className="bg-white border border-sand rounded-2xl p-5 flex items-start gap-5 flex-wrap">
        <div className={`w-24 h-24 rounded-2xl grid place-items-center ${gradeClass} shrink-0`}>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-widest opacity-90">{t("rdash.note")}</div>
            <div className="font-display text-4xl font-bold">{data.grade}</div>
          </div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs uppercase tracking-widest text-brand-red font-semibold flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> {t("rdash.healthTitle")}
          </div>
          <div className="font-display text-3xl font-semibold">{data.score}/100</div>
          <div className="text-sm text-mute">{t("rdash.dishesAnalyzed", { n: data.total_dishes })}</div>
          <div className="mt-2 h-2 bg-elevated rounded-full overflow-hidden">
            <div
              className="h-full transition-all bg-gradient-to-r from-brand-red via-brand-yellow to-brand-green"
              style={{ width: `${data.score}%` }}
              data-testid="health-score-bar"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(data.breakdown || []).map((b) => {
          const pct = Math.round((b.value / b.max) * 100);
          return (
            <div key={b.key} className="bg-white border border-sand rounded-2xl p-4" data-testid={`health-breakdown-${b.key}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-semibold">{b.label}</div>
                <div className="text-xs font-mono text-mute">{b.value}/{b.max}</div>
              </div>
              <div className="text-xs text-mute">{b.detail}</div>
              <div className="mt-2 h-1.5 bg-elevated rounded-full overflow-hidden">
                <div
                  className={`h-full ${pct >= 75 ? "bg-brand-green" : pct >= 40 ? "bg-brand-yellow" : "bg-brand-red"}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-[#FFF7E6] border border-brand-yellow/40 rounded-2xl p-5" data-testid="health-tips">
        <div className="flex items-center gap-2 mb-2">
          <Lightbulb className="w-4 h-4 text-[#B57F00]" />
          <div className="font-semibold text-sm">{t("rdash.healthTips")}</div>
        </div>
        <ul className="space-y-1.5">
          {(data.tips || []).map((t, i) => (
            <li key={i} className="text-sm text-ink flex gap-2">
              <span className="text-[#B57F00]">→</span>
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
